'use client';

import { useSearchParams } from 'next/navigation';

import type { IconName } from '@eliadmin/core/src/components/nt/ui/icon';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import type { TranslateFn } from '@eliadmin/core/src/hooks/use-translations';
import { useLocalePathname } from '@eliadmin/core/src/hooks/use-locale-pathname';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';

export type AccountPageKey = 'profile' | 'password' | 'orders' | 'favorites';

export type AccountPage = {
  key: AccountPageKey;
  /** Core dictionary key of the label (exact Figma text). */
  label: Parameters<TranslateFn>[0];
  icon: IconName;
  href: () => string;
};

/** The navigation of Figma „Account / Sidebar“ (205:11128), in its order. */
export const ACCOUNT_PAGES: AccountPage[] = [
  { key: 'profile', label: 'text1134', icon: 'user', href: () => ROUTES.PROFILE() },
  { key: 'password', label: 'text115', icon: 'lock-simple', href: () => ROUTES.CHANGE_PASSWORD() },
  { key: 'orders', label: 'text257', icon: 'package', href: () => ROUTES.ORDERS() },
  { key: 'favorites', label: 'text61', icon: 'heart', href: () => ROUTES.FAVORITES() },
];

/** The order detail lives on the orders URL: `/objednavky?objednavka=NT-2026-104512`. */
export const ORDER_PARAM = 'objednavka';

export function orderDetailHref(identifier: string) {
  return `${ROUTES.ORDERS()}?${ORDER_PARAM}=${encodeURIComponent(identifier)}`;
}

/** The return form opened at an order (`ReturnView` reads the same parameter). */
export function orderReturnHref(identifier: string) {
  return `${ROUTES.RETURN()}?${ORDER_PARAM}=${encodeURIComponent(identifier)}`;
}

/**
 * The account page of the current URL. The core return form (allowed by the gate) belongs to the
 * orders – its „Vrátiť tovar“ starts from an order – so it keeps that item active.
 */
export function useAccountPage() {
  const pathname = useLocalePathname();
  const page = ACCOUNT_PAGES.find(item => arePathsEqual(item.href(), pathname));
  if (page) return { page, isReturn: false };
  const isReturn = arePathsEqual(ROUTES.RETURN(), pathname);
  return { page: isReturn ? ACCOUNT_PAGES[2] : null, isReturn };
}

/** Order number of the detail view, `null` on the list. Needs a `<Suspense>` above it. */
export function useOrderParam() {
  return useSearchParams().get(ORDER_PARAM);
}
